import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import cn from "classnames";
import styles from "./ProfileInformation.module.sass";
import Item from "../Item";
import Dropdown from "../../../components/Dropdown";


import StudentSearchBar from "../../../components/StudentSearchBar";
import CenterSearchBar from "../../../components/CenterSearchBar";
import { getFormations } from '../../../services/formation.service';
import { getPromotionsByFormation } from '../../../services/promotion.service';

const ProfileInformation = ({ className, data, updateData, currentStep }) => {
  const location = useLocation();
  const [content, setContent] = useState();
  
  // Détecter le type de dossier depuis l'URL
  const getDossierTypeFromUrl = () => {
    const pathname = location.pathname;
    if (pathname.includes('/create/manual')) {
      return 'MANUEL';
    } else if (pathname.includes('/create/auto')) {
      return 'AUTONOME';
    }
    // Par défaut, retourner AUTONOME pour la route /create
    return 'AUTONOME';
  };
  
  const [dossierType, setDossierType] = useState(getDossierTypeFromUrl());
  const [formations, setFormations] = useState([]);
  const [loadingFormations, setLoadingFormations] = useState(false);
  const [promotions, setPromotions] = useState([]);
  const [loadingPromotions, setLoadingPromotions] = useState(false);
  const [selectedFormation, setSelectedFormation] = useState(null);
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [preferences, setPreferences] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [selectedContractType, setSelectedContractType] = useState(null);


  // Initialiser les valeurs depuis les données existantes
  useEffect(() => {
    if (data?.formation) {
      setSelectedFormation(data.formation);
    }
    if (data?.promotion) {
      setSelectedPromotion(data.promotion);
    }
    if (data?.student) {
      setSelectedStudent(data.student);
    }
    
    // Mettre à jour le type de dossier depuis l'URL
    const urlDossierType = getDossierTypeFromUrl();
    setDossierType(urlDossierType);
    if (updateData) {
      updateData('dossierType', urlDossierType);
    }
  }, [data, location.pathname]);

  useEffect(() => {
    const fetchFormations = async () => {
      setLoadingFormations(true);
      try {
        const formationData = await getFormations();
        setFormations(formationData || []);
        
        // Si une formation est déjà sélectionnée, récupérer ses promotions
        if (data?.formation) {
          setLoadingPromotions(true);
          try {
            const promotionData = await getPromotionsByFormation(data.formation);
            setPromotions(promotionData || []);
          } catch (error) {
            console.error("Erreur lors de la récupération des promotions", error);
            setPromotions([]);
          } finally {
            setLoadingPromotions(false);
          }
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des formations", error);
        setFormations([]);
      } finally {
        setLoadingFormations(false);
      }
    };

    fetchFormations();
  }, []);

  // Créer les options pour le dropdown des formations
  const formationOptions = formations.map(f => ({ value: f.id, title: f.exact_title_of_qualification }));
  
  // Créer les options pour le dropdown des promotions
  const promotionOptions = promotions.map(p => ({ value: p.id, title: p.title_promotion }));

  // Gérer la sélection d'une formation et récupérer ses promotions
  const handleFormationChange = async (formationId) => {
    setSelectedFormation(formationId);
    setSelectedPromotion(null); // Reset promotion when formation changes
    if (updateData) {
      updateData('formation', formationId);
      updateData('promotion', null); // Reset promotion in data too
    }

    if (formationId) {
      setLoadingPromotions(true);
      try {
        const promotionData = await getPromotionsByFormation(formationId);
        setPromotions(promotionData || []);
      } catch (error) {
        console.error("Erreur lors de la récupération des promotions", error);
        setPromotions([]);
      } finally {
        setLoadingPromotions(false);
      }
    } else {
      setPromotions([]);
    }
  };

  // Gérer la sélection d'un étudiant
  const handleSelectStudent = (student) => {
    setSelectedStudent(student);
    // Mettre à jour les données du dossier avec les informations de l'étudiant
    if (updateData) {
      updateData('student', {
        id: student?.id,
        firstName: student?.first_name,
        lastName: student?.last_name,
        email: student?.email,
        phone: student?.phone,
        birthDate: student?.birth_date,
        type: student?.type,
        status: student?.status,
        formation: student?.expand?.formation
      });
    }
  };

  // Gérer la sélection d'un centre
  const handleSelectCenter = (center) => {
    setSelectedCenter(center);
    // Mettre à jour les données du dossier avec les informations du centre
    if (updateData) {
      updateData('center', {
        id: center?.id,
        name: center?.name,
        address: center?.address,
        address_complement: center?.address_complement,
        zip_code: center?.zip_code,
        city: center?.city,
        department: center?.department,
        country: center?.country,
        phone: center?.phone,
        email: center?.email
      });
    }
  };


  return (
    <Item
      className={cn(styles.card, className)}
      title="Création d'un dossier étudiant"
      classTitle="title-green"
    >
  
      <div className={styles.fieldset}>
        <StudentSearchBar
          label="Sélectionner un étudiant existant"
          placeholder="Rechercher par nom, email ou téléphone (min. 2 caractères)"
          onSelectStudent={handleSelectStudent}
          showDetails={true}
          required={true}
        /> 

        <CenterSearchBar
          label="Sélectionner un centre de formation"
          placeholder="Rechercher un centre (min. 2 caractères)"
          onSelectCenter={handleSelectCenter}
          showDetails={true}
          required={dossierType === 'MANUEL'}
        />

        <div className={styles.group}>
          <div className={styles.field}>
              {loadingFormations ? (
                  <p>Chargement des formations...</p>
              ) : (
                  <Dropdown
                      className={styles.dropdown}
                      label={dossierType === 'MANUEL' ? "Sélectionner une formation *" : "Formation"}
                      name="formation"
                      options={formationOptions}
                      value={formationOptions.find(opt => opt.value === selectedFormation) || ''}
                      setValue={(option) => handleFormationChange(option.value)}
                      required={dossierType === 'MANUEL'}
                  />
              )}
          </div>

          <div className={styles.field}>
              {loadingPromotions ? (
                  <p>Chargement des promotions...</p>
              ) : (
                  <Dropdown
                      className={styles.dropdown}
                      label={dossierType === 'MANUEL' ? "Sélectionner une promotion *" : "Promotion"}
                      name="promotion"
                      options={promotionOptions}
                      value={promotionOptions.find(opt => opt.value === selectedPromotion) || ''}
                      setValue={(option) => {
                          setSelectedPromotion(option.value);
                          updateData && updateData('promotion', option.value);
                      }}
                      disabled={!selectedFormation || promotions.length === 0}
                      placeholder={!selectedFormation ? "Sélectionnez d'abord une formation" : "Aucune promotion disponible"}
                      required={dossierType === 'MANUEL'}
                  />
              )}
          </div>
        </div>

      </div>
    </Item>
  );
};

export default ProfileInformation;
